import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  useVendorContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
} from '@/api/hooks/useVendors';
import type { VendorContact } from '@/api/types/vendor.types';
import { useAuthStore } from '@/store/auth.store';

// ── Contact role label map ────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  security: 'Security',
  technical: 'Technical',
  legal: 'Legal',
  executive: 'Executive',
  billing: 'Billing',
  operations: 'Operations',
};

// ── AddCrmContactModal ────────────────────────────────────────────────────────

interface AddCrmContactModalProps {
  vendorId: string;
  open: boolean;
  onClose: () => void;
}

function AddCrmContactModal({ vendorId, open, onClose }: AddCrmContactModalProps) {
  const create = useCreateContact(vendorId);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', title: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await create.mutateAsync({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      title: form.title.trim() || undefined,
    });
    setForm({ firstName: '', lastName: '', email: '', title: '' });
    onClose();
  };

  const handleClose = () => {
    setForm({ firstName: '', lastName: '', email: '', title: '' });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Add CRM Contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="crm-add-first">First name <span className="text-danger">*</span></Label>
            <Input
              id="crm-add-first"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              required
              placeholder="Jane"
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="crm-add-last">Last name <span className="text-danger">*</span></Label>
            <Input
              id="crm-add-last"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              required
              placeholder="Smith"
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="crm-add-email">Email <span className="text-danger">*</span></Label>
            <Input
              id="crm-add-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              placeholder="jane@vendor.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="crm-add-title">
              Role / title <span className="text-text-tertiary text-xs font-normal">(optional)</span>
            </Label>
            <Input
              id="crm-add-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Security Engineer"
              maxLength={150}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={handleClose} disabled={create.isPending}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add Contact'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── EditCrmContactModal ───────────────────────────────────────────────────────

interface EditCrmContactModalProps {
  vendorId: string;
  contact: VendorContact;
  open: boolean;
  onClose: () => void;
}

function EditCrmContactModal({ vendorId, contact, open, onClose }: EditCrmContactModalProps) {
  const update = useUpdateContact(vendorId);
  const [form, setForm] = useState({
    firstName: contact.first_name,
    lastName: contact.last_name,
    email: contact.email,
    title: contact.title ?? '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await update.mutateAsync({
      contactId: contact.id,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      title: form.title.trim() || undefined,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit CRM Contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="crm-edit-first">First name <span className="text-danger">*</span></Label>
            <Input
              id="crm-edit-first"
              value={form.firstName}
              onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
              required
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="crm-edit-last">Last name <span className="text-danger">*</span></Label>
            <Input
              id="crm-edit-last"
              value={form.lastName}
              onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
              required
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="crm-edit-email">Email <span className="text-danger">*</span></Label>
            <Input
              id="crm-edit-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="crm-edit-title">
              Role / title <span className="text-text-tertiary text-xs font-normal">(optional)</span>
            </Label>
            <Input
              id="crm-edit-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={150}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose} disabled={update.isPending}>Cancel</Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── DeleteCrmContactConfirmDialog ─────────────────────────────────────────────

interface DeleteCrmContactConfirmDialogProps {
  vendorId: string;
  contact: VendorContact;
  open: boolean;
  onClose: () => void;
}

function DeleteCrmContactConfirmDialog({ vendorId, contact, open, onClose }: DeleteCrmContactConfirmDialogProps) {
  const remove = useDeleteContact(vendorId);

  const handleConfirm = () => {
    remove.mutate(contact.id, { onSettled: onClose });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Remove CRM contact?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-text-secondary">
          <strong className="text-text-primary">{contact.first_name} {contact.last_name}</strong> will no
          longer appear in the CRM directory. You can re-add them later.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={remove.isPending}>Cancel</Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={remove.isPending}>
            {remove.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CrmContactsTab ────────────────────────────────────────────────────────────

export function CrmContactsTab({ vendorId }: { vendorId: string }) {
  const { data: contacts = [], isLoading } = useVendorContacts(vendorId);
  const [addOpen, setAddOpen] = useState(false);
  const [editContact, setEditContact] = useState<VendorContact | null>(null);
  const [deleteContact, setDeleteContact] = useState<VendorContact | null>(null);
  const { hasAnyRole } = useAuthStore();
  const canManage = hasAnyRole(['risk_manager', 'ciso', 'super_admin']);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-text-secondary">
            CRM contacts are reference entries for procurement, billing, and security contacts. They do not
            have portal access — use the <strong className="text-text-primary font-medium">Vendor Contacts</strong> tab for that.
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add CRM Contact
          </Button>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 rounded" />)}
        </div>
      )}

      {/* Empty */}
      {!isLoading && contacts.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-subtle bg-bg-surface/50 px-6 py-12 text-center">
          <p className="text-sm font-medium text-text-primary">No CRM contacts</p>
          <p className="mt-1 text-xs text-text-secondary">
            Add contacts from this vendor's procurement, billing, or security teams.
          </p>
          {canManage && (
            <Button size="sm" className="mt-4" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add CRM Contact
            </Button>
          )}
        </div>
      )}

      {/* Table */}
      {!isLoading && contacts.length > 0 && (
        <div className="rounded-lg border border-border-subtle overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-bg-surface-2 border-b border-border-subtle">
              <tr>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Name</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Email</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Role</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary">Title</th>
                <th className="py-2 px-4 text-xs font-medium text-text-secondary" />
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id} className="border-b border-border-subtle hover:bg-bg-surface-2/50 transition-colors last:border-0">
                  <td className="py-3 px-4 font-medium text-text-primary">
                    {c.first_name} {c.last_name}
                  </td>
                  <td className="py-3 px-4 text-text-secondary">{c.email}</td>
                  <td className="py-3 px-4">
                    <Badge variant="muted">{ROLE_LABELS[c.contact_role] ?? c.contact_role}</Badge>
                  </td>
                  <td className="py-3 px-4 text-text-secondary">{c.title ?? '—'}</td>
                  <td className="py-3 px-4 text-right">
                    {canManage && (
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          aria-label="Edit contact"
                          title="Edit contact"
                          onClick={() => setEditContact(c)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          aria-label="Remove contact"
                          title="Remove contact"
                          onClick={() => setDeleteContact(c)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <AddCrmContactModal vendorId={vendorId} open={addOpen} onClose={() => setAddOpen(false)} />
      {editContact && (
        <EditCrmContactModal
          vendorId={vendorId}
          contact={editContact}
          open
          onClose={() => setEditContact(null)}
        />
      )}
      {deleteContact && (
        <DeleteCrmContactConfirmDialog
          vendorId={vendorId}
          contact={deleteContact}
          open
          onClose={() => setDeleteContact(null)}
        />
      )}
    </div>
  );
}
